/** @module PI-ModuleSpecification/WebAccess/Specification/CreateComposition */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-construct",
	"dojo/promise/all",
	"dojo/sniff",
	"dojo/store/Memory",
	"dojo/store/Observable",
	"dojo/when",
	"dijit/form/Button",
	"dijit/MenuItem",
	"dijit/ToolbarSeparator",
	"dgrid/editor",
	"dgrid/OnDemandGrid",
	"csi",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/comm",
	"csi/html",
	"csi/dgrid/Manager",
	"csi/action/Controller",
	"csi/locale",
	"csi/message",
	"csi/sessionConstants",
	"csi/store/_base",
	"csi/store/Names",
	"csi/store/Nodes",
	"csi/widget/_DialogMixin",
	"csi/widget/CheckBox",
	"csi/widget/CheckedMultiSelectDropdown",
	"csi/widget/Dialog",
	"csi/widget/FilteringSelect",
	"csi/widget/InvalidMessage",
	"csi/widget/MessageDialog",
	"csi/widget/Select",
	"csi/widget/SelectMenuRemovable",
	"csi/widget/SelectMenuTextPrefixRemovable",
	"csi/widget/NumberTextBox",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/Search"
], function(declare, lang, domConstruct, all, has, Memory, Observable, when, Button, MenuItem, ToolbarSeparator, editor, OnDemandGrid, csi, storage, _, comm, html, Manager, Controller, locale, message, sessionConstants, csiStore, Names, Nodes, _DialogMixin, CheckBox, CheckedMultiSelectDropdown, Dialog,
	FilteringSelect, InvalidMessage, MessageDialog, Select, SelectMenuRemovable, SelectMenuTextPrefixRemovable, NumberTextBox, xml, Query, Search) {

	const LABEL_PROPERTY = "_label";

	var COMP = "Composition";
	var COMPDATA = "CompositionData";
	var COMPELEMAP = "CompositionElementsMap";
	var COMPPLACEMENT = "CompositionPlacement";
	var COMPLINES = "CompositionLines";
	var LANGUAGE = "Language";
	var STORAGE_LANGUAGE = "EditCompositionLanguage";
	var COMPEMPTY = "empty";
	var BASE_COMPOSITION = 0;

	/**
	 * Object: _compValues Stores all of the different composition values for all languages
	 */
	var _compValues = {};
	var _language;
	var _renderLanguage;
	var _languageStore;
	var _libraryStore;
	var _libraryTotal;
	var _placementStore;
	var _resetException = false;
	var _salesRegionStore;
	var _applyBtn;
	/**
	 * Array: _mapValueArray Stores all map values for placements and materials
	 */
	var _mapValueArray = [];

	/**
	 * Returns the language Sys id
	 * @param {String} language
	 */
	function _getSysId(language) {
		var l = _languageStore.get(language || _language);
		return (l && l.SysID) || 0;
	}

	/**
	 * Returns the language id
	 * @param {String number} Id
	 */
	function _getLanguageId(Id) {
		var item = _languageStore.query({SysID: Id});
		// if there are no items or item is disabled, then return empty url
		if (item.length) {
			const lang = item[0];
			return lang._disabled ? csi.EMPTY_URL : lang[_languageStore.idProperty];
		} 
		return csi.EMPTY_URL
	}

	/**
	 * Validate base composition is empty or not
	 * @param {string} value
	 * @return {boolean} true if base is empty
	 */
	function _isBaseCompositionEmpty() {
		return (!_compValues[BASE_COMPOSITION] || _compValues[BASE_COMPOSITION] == "[\"" + COMPEMPTY + "\"]");
	}

	/**
	 * validate value is exception or not
	 * @param {string} value
	 * @return {boolean} true if is exception
	 */
	function _isException(value) {
		return !!_compValues[parseInt(_getSysId(value))];
	}


	/**
	* enable or disable the state of the apply button based on selection of language check boxes
	* @param {Object []} checkBoxArray - array of checkboxes
	* @param {boolean | undefined} flag - bypass the loop and toggle classes based on boolean value
	* @param {string []} gridItems - filtered grid items
	*/
	function _toggleApplyButton(checkBoxArray, flag, gridItems) {
		var enable;
		if (flag == null) {
			enable = _.some(checkBoxArray, function(chkBox) {
				if (!gridItems || _.contains(gridItems, chkBox.get("languageId"))) {
					return chkBox.get("checked");
				}
			});
		} else {
			enable = flag;
		}
		if (_applyBtn) {
			_applyBtn.set("disabled", !enable);
		}
	}

	/**
	 * if no render language, set the given urlLanguage or if render language is defined, set both placement and material dropDown items text to renderLanguage text.
	 * @param {string} urlLanguage
	 * @param {string} renderLanguage
	 */
	function _setLanguage(urlLanguage, renderLanguage) {
		const inactive = " (" + locale.resource("Inactive") + ")";

		_renderLanguage = renderLanguage;
		var language = renderLanguage || urlLanguage;
		var langItem = _languageStore.get(language);
		var langName = langItem && langItem[_languageStore.labelProperty];
		var langSysID = langItem && langItem.SysID;

		_language = urlLanguage || csi.EMPTY_URL;
		storage.set(STORAGE_LANGUAGE, _language);

		_libraryStore.query().forEach(function(item) {
			var s = langName && item.Languages[langSysID];

			item[LABEL_PROPERTY] = item.$Name + (s ? (" - " + s) : "");

			if (!item.OKforStyle) {
				item[LABEL_PROPERTY] += inactive;
			}
		});

		_placementStore.query().forEach(function(item) {
			var s = langName && item.Languages[langSysID];

			item[LABEL_PROPERTY] = item.$Name + (s ? (" - " + s) : "");

			if (!item.Active) {
				item[LABEL_PROPERTY] += inactive;
			}
		});
	}

	var showDialog = declare([MessageDialog], {
		selectionBox: null,
		grid: null,
		buildRendering: function() {
			this.inherited(arguments);

			if (this.selectionBox) {
				html.create("div", {innerHTML: locale.resource("CompositionSalesRegion"), "class": "csi-comp-editor-apply-filter-text"}, this.containerNode);
				this.addChild(this.selectionBox);
			}

			this.addChild(this.grid);
			this.grid.refresh();
		}
	});

	var _Dialog = declare([Dialog, _DialogMixin], {
		autofocus: false,
		baseClass: "csi-comp-editor",
		destroyOnHide: true,
		dialogActions: true,
		dirty: false,
		init: false,
		closeOnSave: true,
		savedDataColumnCount: 0,
		submitResource: "Save_Close",
		suffixHtml: "",
		applyToHistory: null,

		/** @property {object} itemSelected - stores seleted checkbox items so it can be maintained on filter change*/
		itemSelected: null,

		/** @property {object} initialComp - previous composition inorder to fall back to if filter changed before saving*/
		initialComp: null,

		/**
		 * Add browser view to dialog
		 */
		buildRendering: function() {
			var self = this;

			this.inherited(arguments);
			this.set("title", locale.resource("CompositionEditor"));
			this.createIntroNode();
			this.applyToHistory = {};
			this.itemSelected = {};

			this.invalidMessage = new InvalidMessage().placeAt(this.introNode);

			var suffixDom = html.create("span", {
				"class": "csi-menu-italic",
				innerText: "(" + locale.resource("CompositionEditorException") + ")"
			});
			this.suffixHtml = " " + suffixDom.outerHTML;

			var grid = this.grid = new Manager({
				allowNew: function() {
					return !!_libraryTotal;
				},
				"class": "csi-comp-editor-grid",
				keepScrollPosition: true,
				store: self.listStore,
				query: {Language: _language},
				newToolbarParam: {
					// new line buttons
					buttons: [{
						dropDown: true,
						name: "CompositionNewLine",
						showLabel: true,
						children: [{
							label: locale.resource("CompositionNewPlacement"),
							callback: "newRowPlacement"
						},
						{
							label: locale.resource("CompositionNewMaterial"),
							callback: "newRowMaterial"
						}]
					}],
					"class": "dgrid-cell-padding"
				},
				customActions: [{
					// new column items buttons
					"class": "csi-comp-add-new-itembtn",
					name: "Add",
					icon: "add",
					children: [{
						label: locale.resource("CompositionNewPlacement"),
						callback: "onAddPlacement",
						disabledCallback: _.bind(this._disableMenuItemCheck, this)
					},
					{
						label: locale.resource("CompositionNewMaterial"),
						callback: "onAddMaterial",
						disabledCallback: _.bind(this._disableMenuItemCheck, this)
					},
					{
						label: locale.resource("CompositionInsertPlacement"),
						callback: "insertRowPlacement"
					},
					{
						label: locale.resource("CompositionInsertMaterial"),
						callback: "insertRowMaterial"
					}]
				}],

				newRowPlacement: function(row, param) {
					this._newRow(this.onNew(SelectMenuRemovable));
				},

				newRowMaterial: function(row, param) {
					this._newRow(this.onNew(SelectMenuTextPrefixRemovable));
				},

				insertRowPlacement: function(row, param) {
					this._newRow(this.onNew(SelectMenuRemovable), row.data);
				},

				insertRowMaterial: function(row, param) {
					this._newRow(this.onNew(SelectMenuTextPrefixRemovable), row.data);
				},

				onAddPlacement: function(row, param, bNotRefresh, pos) {
					return this._addWidget(row, param, bNotRefresh, SelectMenuRemovable, pos);
				},

				onAddMaterial: function(row, param, bNotRefresh, pos) {
					return this._addWidget(row, param, bNotRefresh, SelectMenuTextPrefixRemovable, pos);
				},

				noDataMessage: _libraryTotal ? null : locale.resource("EditCompositionEmpty"),
				columns: this.getColumnArray(),
				onNew: lang.hitch(this, this._onNewComposition),
				onDelete: lang.hitch(this, this._onDeleteComposition),

				_addWidget: function(row, param, bNotRefresh, w, pos) {
					if (param && param.value && !(w === SelectMenuRemovable || typeof param.value === "object")) {
						// make sure value is of right format for composition
						param.value = {
							value: param.value,
							textValue: param.textValue
						};
					}

					var editorArgs = self.getEditorArgs(w, param);

					if (row.data.widgets.length < self.getColumnCount() || pos) {
						var widget = new w(editorArgs);
						if (param.value) {
							widget.set("value", param.value);
						}

						//array position is for fixing ie defect
						if (pos || pos === "0") {
							row.data.widgets[pos] = widget;
						} else {
							row.data.widgets.push(widget);
						}

						if (!bNotRefresh) {
							this.refresh();
						}

						return widget;
					}
				},

				_newRow: function(item, currentRowData) {
					item[this.store.idProperty] = comm.createUrl();
					this._addItem(item, currentRowData ? {before: currentRowData} : null);
					self.setCompValue();
					self.setMsg();
					// Grid does not seem to correctly monitor inserting into the list,
					// but this only seems to happen when inserting before current row.
					if (currentRowData) {
						this.refresh();
					}
				}
			});

			$(grid.domNode).css({"min-width": (277 * self.getColumnCount()) + "px"});

			this.addChild(grid);
		},

		/**
		 * Callback used in determining if toolbar dropdown menu item should be disabled.
		 * @param {Object} data - used to check if row has max items
		 * @return {boolean} indicates if row has max allowed items
		 */
		_disableMenuItemCheck: function(data) {
			return data.widgets.length >= this.getColumnCount();
		},

		/**
		 * Creates new item and adds it to store
		 * @param {String} language - url
		 * @param {Array} item
		 */
		createNewItem: function(language, item) {
			var listData = {
				SysID: _getSysId(language),
				Language: language,
				CompositionData: item
			};

			listData[this.listStore.idProperty] = comm.createUrl();
			this.listStore.add(listData);
		},

		/**
		 * Returns the column counts of the composition editor
		 */
		getColumnCount: function() {
			//if saved data is having more columns then use that instead of configured count to prevent loss of data
			return Math.max(sessionConstants.siteMaxCompositionColumns, this.savedDataColumnCount);
		},

		/**
		 * Assigns the heading renderer and returns the column data
		 */
		getColumnArray: function() {
			var columnArray = [];
			var columnData;
			var self = this;
			var i;

			this.listStore.query().forEach(function(n) {
				//set the min column count from saved data if exists
				if (n.CompositionData.length > self.savedDataColumnCount) {
					self.savedDataColumnCount = n.CompositionData.length;
				}
			});

			var columnCount = this.getColumnCount();

			for (i = 0; i < columnCount; i++) {
				columnData = {renderCell: this._renderWidgetCell};

				if (columnCount > 1) {
					if (i === 0) {
						//add language select buttons to header
						columnData.renderHeaderCell = lang.hitch(this, this._renderCompositionHeader);
					}
					if (i === (columnCount - 1)) {
						columnData.renderHeaderCell = lang.hitch(this, this._renderCompositionResetHeader);
					}
				} else {
					columnData.renderHeaderCell = lang.hitch(this, function(th) {
						this._renderCompositionHeader(th);
						this._renderCompositionResetHeader(th);
					});
				}

				columnArray.push(columnData);
			}

			return columnArray;
		},

		/**
		 * Prepares the arguments for placement and material widgets
		 * @param {Widget} w - widget name
		 */
		getEditorArgs: function(w, param) {
			var self = this;
			var bPlacement = (w === SelectMenuRemovable);
			var id = bPlacement ? (param && param.value) : (param && param.value && param.value.value);
			var sourceStore = bPlacement ? _placementStore : _libraryStore;
			var queryAttr = bPlacement ? "Active" : "OKforStyle";
			var queryObject = csi.objectNew(queryAttr, true);
			var data = sourceStore.query(queryObject);

			if (id) {
				var node = sourceStore.get(id);
				if (node && !node[queryAttr]) {
					data.push(node);
				}
			}

			var store = new Memory({
				data: data,
				idProperty: sourceStore.idProperty,
				labelProperty: LABEL_PROPERTY
			});

			var widgetArgs = {
				textSuffix: (param && param.textSuffix) || "%",
				textValue: (param && param.textValue) || "",
				value: (param && param.value) || null,
				store: store,

				onChangeSelect: function(val) {
					//on change values trigger exception message change
					if (this.focused) {
						// do not call update message and set dirty on widget initialization
						self._setDirty(null, true);
					}
				},
				onChangeText: function() {
					if (this._hasBeenBlurred) {
						// do not call update message and set dirty on widget initialization
						self._setDirty(null, true);
					}
				},
				onDelete: function() {
					// deletes the widget and refresh grid to rearange the rest of the widget to fill in the  spaces.
					var grid = self.grid || self;
					var rowData = grid.row(this.domNode);
					var value = {type: this.widgetType, value: this.get("value")};

					if (value.type == "Composition") {
						value.textSuffix = this.textSuffix;
						value.textValue = this.textValue;
					}

					//delete the corresponding value from store
					_.each(rowData.data.CompositionData, function(val, i) {
						if (JSON.stringify(val) == JSON.stringify(value)) {
							rowData.data.CompositionData.splice(i, 1);
						}
					});

					rowData.data.widgets = _.difference(rowData.data.widgets, [this]);

					if (!rowData.data.widgets.length) {
						// remove the row data item from store when the row's last value is deleted
						self.listStore.remove(rowData.id);
					}
					self._setDirty(true, true);
				},
				widgetType: (param && param.type) || (w === SelectMenuRemovable ? COMPPLACEMENT : COMP)
			};

			return widgetArgs;
		},

		/**
		 * returns formatted label
		 * @param {String} label
		 * @param {Boolean} bSuffix
		 */
		getFormattedLabel: function(label, bSuffix) {
			var formatedLabel = label + (bSuffix ? this.suffixHtml : "");
			return formatedLabel;
		},

		/**
		 * Add preview Button
		 * @param {DomNode} dom
		 */
		onRenderDialogActions: function(dom) {
			var self = this;
			new Button({
				"class": html.CSS_BUTTON_SECONDARY,
				label: _.escape(locale.resource("Save")),
				onClick: function(e) {
					self.closeOnSave = false;
					self._onSubmit(e);
				}
			}).placeAt(dom, "first");
			new Button({
				"class": html.CSS_BUTTON_SECONDARY,
				label: _.escape(locale.resource("Preview")),
				onClick: function() {self._onPreview();}
			}).placeAt(dom, "first");
		},

		/**
		 * Initializes composition values and sets the messages
		 */
		postCreate: function() {
			this.inherited(arguments);
			this.setCompValue();
			this.init = false;
			this.setMsg();
			this.initialComp = _.clone(_compValues);
		},

		/**
		 * Creates base composition value if it does not exist
		 */
		setBaseCompValue: function() {
			var self = this;
			var compKeys = Object.keys(_compValues);

			if (compKeys && compKeys.length && !_compValues[BASE_COMPOSITION]) {
				_compValues[BASE_COMPOSITION] = _compValues[compKeys[0]];
				var languageId = _getLanguageId(compKeys[0]);
				this.listStore.query({Language: languageId}).forEach(function(item) {
					self.createNewItem(csi.EMPTY_URL, _.clone(item.CompositionData));
				});
				this.setCompValue();
				this.grid.refresh();
			}
		},

		/**
		 * Sets the composition values, creates base composition from existing other language composition if base composition does not exists
		 * @param {Boolean} bDelete
		 */
		setCompValue: function(bDelete, bChanged) {
			var self = this;
			var value = this.get("value");
			var prevValue = _compValues[BASE_COMPOSITION];
			var valueArray = JSON.parse(value);

			if (valueArray.length) {
				//set compValue only if the array is not empty
				_compValues[_getSysId(this.init ? csi.EMPTY_URL : null)] = value;

				if (!this.init && (_language == csi.EMPTY_URL) && (prevValue != _compValues[BASE_COMPOSITION])) {
					//reset base language to update the store values to newly changed values
					if (!bChanged) {
						this._resetComp(0, value);
					}

					_.each(_compValues, function(val1, i) {
						// reset all other exception languages with older base value to newly changed values
						if (i != 0 && prevValue == val1) {
							this._resetComp(i);
						}
					}, this);
				}
			} else if (bDelete) {
				//when the last values is removed from grid, delete the the item from the store
				var item = this.listStore.query({SysID: _getSysId()}).first();
				if (item) {
					var removeId = this.listStore.getIdentity(item);
					this.listStore.remove(removeId);
				}
				_compValues[_getSysId()] = "[\"" + COMPEMPTY + "\"]";
				return;
			}

			//prefill items with default value if its not an exception
			if (!_compValues[_getSysId()] && _compValues[BASE_COMPOSITION]) {
				this.listStore.query({Language: csi.EMPTY_URL}).forEach(function(item) {
					self.createNewItem(_language, _.clone(item.CompositionData));
				});
				// get value to get the newly updated store value
				value = _compValues[BASE_COMPOSITION];
				if (JSON.parse(value).length) {
					//set compValue only if the array is not empty
					_compValues[_getSysId()] = value;
				}
			}
		},

		/**
		 * Sets messages and button texts
		 * @param {String} [msg]
		 */
		setMsg: function(msg) {
			var m = msg || locale.resource(_language == csi.EMPTY_URL ? "CompositionEditorMsgDefault" : "CompositionEditorMsgNoException");
			var icon = "";
			var exception = false;

			if (!_resetException && (_compValues[BASE_COMPOSITION] !== _compValues[_getSysId(_language)] || (_isBaseCompositionEmpty() && _language !== csi.EMPTY_URL))) {
				m = locale.resource("CompositionEditorMsgException") + (msg || "");
				exception = true;
				icon = this.invalidMessage.WARNING;

			}
			_resetException = false;
			this.updateLanguageDropdown();

			//toggle exception button visiblity
			if (this.btnContainer) {
				$(this.btnContainer).toggle(exception);
			}

			this.invalidMessage.set('value', m);
			this.invalidMessage.set("icon", icon);
		},

		/**
		 * updates the language dropdown with exception values
		 * @param {Widget} [dropDown]
		 */
		updateLanguageDropdown: function(dropDown) {
			var widget = dropDown || this.languageDropDown;

			_.each(_compValues, function(val, i) {
				var langId = _getLanguageId(i);

				_languageStore.get(langId).labelSuffix = !!(i != 0 && val != _compValues[BASE_COMPOSITION]);
			});

			if (widget) {
				_.each(widget.getOptions(), function(option) {
					//update the language dropdown label with exception text if its an exception
					option.label = this.getFormattedLabel(option.item.Name, option.item.labelSuffix);
					widget.updateOption(option);
				}, this);
			} else {
				// hide dgrid header row when there is no language
				$(this.containerNode).addClass("csi-dgrid-no-header");
			}
		},

		/**
		 * Grid validation
		 */
		validate: function() {
			if (!this.inherited(arguments)) {
				return false;
			}

			if (!this.grid.validateChildren(true)) {
				return false;
			}

			return true;
		},

		_applyToComp: function() {
			var currentSysid = _getSysId();
			var checkBoxArray = [];
			var self = this;

			function getLanguageData() {
				var data = _languageStore.query(function(val) {
					if (val.SysID && val.SysID != currentSysid) {
						return val;
					}
				});

				return data;
			}

			function getLanguageNameList() {
				var appliedLangString = [];
				_.each(self.applyToHistory[_language], function(Id) {
					var item = _languageStore.query({SysID: Id});
					appliedLangString.push(item[0].Name);
				});
				return _.unique(appliedLangString).join(", ");
			}

			var contentStore = new Names({
				data: getLanguageData()
			});

			const filter = new Query().Attr("Active", xml.OP_EQUALS, true);
			_salesRegionStore = new Nodes({
				bSimpleQueryEngine: true,
				filterQuery: filter,
				sortQuery: new Query().SortByNode(xml.PROP_NAME),
				typeNode: "ProductSalesRegionSpec",
				requestParameters: {
					"Dep.Path": "Child:SellingCountries/Child:Languages"
				}
			});

			function getCheckboxWidget(val, checked, parent) {
				var chkBox = new CheckBox({
					name: "languageCheckBox",
					checked: checked,
					languageId: val,
					onChange: function() {
						// check only the filtered items on grid when "all" is selected and set rest of it to false
						let gridItems = self._getGridItems();
						let chkBoxLanguage = "";
						let currentChkBoxLanguage = this.get("languageId");

						if (!self.itemSelected[_language]) {
							self.itemSelected[_language] = [];
						}
						// handle select/unselect all checkboxes
						_.each(checkBoxArray, (chkBox) => {
							chkBoxLanguage = chkBox.get("languageId");

							if (currentChkBoxLanguage == "") {
								//select or unselect all items
								if (_.contains(gridItems, chkBoxLanguage)) {
									chkBox.set("checked", this.checked);
									if (chkBoxLanguage != "") {
										self.itemSelected[_language] = this.checked ? _.union(self.itemSelected[_language], [chkBoxLanguage]) : [];
									}
								}
							} else {
								var checkBoxStatus = (currentChkBoxLanguage == chkBoxLanguage ? this.get("checked") : chkBox.get("checked"));

								if (!_.contains(gridItems, chkBoxLanguage) && chkBoxLanguage != "") {
									chkBox.set("checked", false);
								} else {
									//checkBoxArray items dont seems to be completely in sync with checkBox so making sure checked status is same
									chkBox.set("checked", checkBoxStatus);
								}

								if (chkBoxLanguage != "") {
									//store selected items to preselect incase filter items in grid are changed
									if (chkBox.get("checked")) {
										self.itemSelected[_language] = _.union(self.itemSelected[_language], [chkBoxLanguage]);
									} else {
										self.itemSelected[_language] = _.without(self.itemSelected[_language], chkBoxLanguage);
									}
								}
							}
						});

						_toggleApplyButton(checkBoxArray, parent && this.checked, gridItems);
					}
				});

				checkBoxArray.push(chkBox);
				return chkBox;
			}

			//set minRowsPerPage to fix the pagination issue where rows get deleted from Dom while scrolling
			this.applyToGrid = new OnDemandGrid({
				"class": "csi-comp-editor-applyto",
				minRowsPerPage: 100,
				columns: [{
					renderCell: function(column, value, node) {
						var w = getCheckboxWidget(value.SysID, (!!_.contains(self.applyToHistory[_language], value.SysID) || _.contains(self.itemSelected[_language], value.SysID)));
						w.placeAt(node);
						$(node).css({width: "29px"});
					},
					renderHeaderCell: function(th) {
						var w = getCheckboxWidget("", false, true);
						w.placeAt(th);
						$(th).css({width: "29px"});
					}
				}, {
					renderCell: function(column, value, node) {
						$(node).html(self.getFormattedLabel(value.Name, value.labelSuffix));
					},
					renderHeaderCell: function(th) {
						$(th).text(locale.resource("CompositionLanguages"));
					}
				}],
				store: contentStore
			});

			// Hide check all items on no sales region as check all means no exception
			var chkBoxAllDomNode = checkBoxArray[0].domNode;
			html.hide(chkBoxAllDomNode);

			var getSalesRegionSelection = new CheckedMultiSelectDropdown({
				searchAttr: "$Name",
				defaultDisplayValue: "Select",
				onChange: (val) => {
					if (val.length) {
						html.show(chkBoxAllDomNode);
					} else {
						html.hide(chkBoxAllDomNode);
					}

					var cacheStore = _salesRegionStore._store;
					const countries = new Set();
					const languages = new Set();

					checkBoxArray[0].set("checked", false);
					for (const regionUrl of val) {
						cacheStore.get(regionUrl).SellingCountries.forEach((countryUrl) => countries.add(countryUrl));
					}

					for (const countryUrl of countries) {
						cacheStore.get(countryUrl).Languages.forEach((languageUrl) => languages.add(languageUrl));
					}

					self.applyToGrid.set("query", languages.size ? (item) => languages.has(contentStore.getIdentity(item)) : null);
				}
			});

			_salesRegionStore.query().then(function(results) {
				var salesRegionNamedStore = new Memory({
					labelProperty: "$Name",
					idProperty: "$URL",
					data: results
				});
				getSalesRegionSelection.set("store", salesRegionNamedStore);
			});


			var applyDialog = new showDialog({
				baseClass: "csi-comp-editor-apply",
				buttons: [MessageDialog.BUTTON_APPLY + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CLOSE],
				title: locale.resource("CompositionApplyToLanguage"),
				grid: this.applyToGrid,
				selectionBox: getSalesRegionSelection,
				onResponse: function(key, values, e) {
					var sysId = _getSysId(_language);
					let checkedvalue = false;
					let checkedLanguage = "";
					let gridItems = self._getGridItems();

					_compValues = _compValues || _.clone(self.initialComp);

					if (key == MessageDialog.BUTTON_APPLY) {
						self.applyToHistory[_language] = [];

						_.each(checkBoxArray, function(chkBox) {
							checkedvalue = chkBox.get("checked");
							checkedLanguage = chkBox.get("languageId");

							if (checkedvalue && checkedLanguage && _.contains(gridItems, checkedLanguage)) {
								self._applyComp(checkedLanguage);
								self.applyToHistory[_language].push(checkedLanguage);
							}
						}, this);

						var applyMsgLang = getLanguageNameList();
						self._setDirty(null, null, applyMsgLang ? "\n" + locale.resource("CompositionEditorMsgApplyList") + applyMsgLang : "");
					} else {
						self.dirty = false;
					}
				}
			});

			applyDialog.set("intro", locale.resource("CompositionEditorMsgApplyto"));
			// get the apply button from the dialog
			_applyBtn = applyDialog.getActionButton(MessageDialog.BUTTON_APPLY);
			_toggleApplyButton(checkBoxArray);
			applyDialog.show();
		},

		_applyComp: function(sysId) {
			var sourceId = _getSysId(_language);
			var l = _getLanguageId(sysId);
			var self = this;

			this.listStore.query({Language: l}).forEach(function(val) {
				//remove all values of selected language
				self.listStore.remove(val[self.listStore.idProperty]);
			});

			this.listStore.query({Language: _language}).forEach(function(val) {
				//repopulate all values from current language
				self.createNewItem(l, _.clone(val.CompositionData));
			});

			_compValues[sysId] = _compValues[sourceId];
		},

		/**
		 * Returns the index value of placement and material maps from map value array
		 * Adds to _mapValueArray is its a new item
		 * @param {String/Obj} val
		 */
		_getMapValue: function(val) {
			var value = typeof val === "object" ? val.value : val;

			if (value) {
				if (!_.contains(_mapValueArray, value)) {
					_mapValueArray.push(value);
				}
				return _.indexOf(_mapValueArray, value);
			}
		},

		_getGridItems: function() {
			return _.pluck(this.applyToGrid._rowIdToObject, "SysID");
		},

		/**
		 * Returns value of current active widgets from grid
		 * Process the values and returns as a string
		 */
		_getValueAttr: function() {
			var res = [];
			var value;
			var results = this.listStore.query({Language: this.init ? csi.EMPTY_URL : _language});

			_.each(results, function(val) {
				var rowArray = [];
				var compositeData = [];
				var compValue = {};
				var valueData = {};

				if (val.widgets) {
					_.each(val.widgets, function(w) {
						value = w.getValue();
						if (!value) {
							return;
						}

						valueData = {
							type: w.widgetType,
							value: this._getMapValue(value)
						};

						compValue = {
							type: w.widgetType,
							value: value
						};

						if (w.widgetType === COMP && value.textValue != "") {
							valueData.textSuffix = w.textSuffix;
							valueData.textValue = value.textValue;
							compValue.textSuffix = w.textSuffix;
							compValue.textValue = value.textValue;
						}

						//if no value then don't add the data
						if (valueData.value != null) {
							//create CompositeValues for store
							compositeData.push(compValue);
							rowArray.push(valueData);
						}
					}, this);

					if (val.widgets.length && compositeData) {
						val.CompositionData = compositeData;
					}
				} else {
					_.each(val.CompositionData, function(item) {
						//clone so it won't affect CompositionData values
						valueData = _.clone(item);
						valueData.value = this._getMapValue(item.value);
						rowArray.push(valueData);
					}, this);
				}

				if (rowArray.length) {
					res.push(rowArray);
				}
			}, this);

			return JSON.stringify(res);
		},

		/**
		 * Handles language change actions
		 * @param {string} value
		 */
		_onChangeLanguage: function(value) {
			if (!this.validate()) {
				this.languageDropDown.set("value", _language);
			} else if (this.dirty && (value !== _language || (_renderLanguage && value !== _renderLanguage))) {
				this._promptSaveDialog(value);
			} else {
				this._onChangeLanguagePartII(value);
			}
		},

		/**
		 * Handles language change actions and validate exception
		 * @param {string} value
		 */
		_onChangeLanguagePartII: function(value) {
			// validate base and value to display exception dialog
			if (_isBaseCompositionEmpty() && value != csi.EMPTY_URL && !_isException(value)) {
				this._promptExceptionDialog(value);
			} else if (value !== _language || (_renderLanguage && value !== _renderLanguage)) {
				this._changeLanguage(value); // pass through
			}
		},


		/**
		 * Display save dialog and handle user action
		 * @param {String} value
		 */
		_promptSaveDialog: function(value) {
			var self = this;
			new MessageDialog({
				buttons: [MessageDialog.BUTTON_SAVE + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL],
				message: locale.resource("CompositionEditorMsgUnsaved"),
				title: locale.resource("CompositionSaveChanges"),
				onResponse: function(key, values, e) {
					var sysId = _getSysId(_language);

					if (key == MessageDialog.BUTTON_SAVE) {
						self.closeOnSave = false;
						self._onSubmit(e);
						self.savedData[sysId] = _compValues[sysId] || _compValues[BASE_COMPOSITION];
						self.dirty = false;
						self._onChangeLanguagePartII(value);
					}
					if (key == MessageDialog.BUTTON_CANCEL) {
						self.languageDropDown.set("value", _language);
					}
				}
			}).show();
		},

		/**
		 * Display exception dialog and handle user action
		 * @param {String} value
		 */
		_promptExceptionDialog: function(value) {
			var self = this;
			new MessageDialog({
				buttons: ["EXCEPTION", "BASE_COMP", MessageDialog.BUTTON_CANCEL],
				message: locale.resource("CompositionEditorMsgNoBase"),
				title: locale.resource("CompositionNewComp"),
				onResponse: function(key, values, e) {
					if (key == "BASE_COMP") {
						_resetException = true;
						self._changeLanguage(csi.EMPTY_URL); // toggle back to Base
					} else if (key == "EXCEPTION") {
						self._changeLanguage(value); // pass through
					} else {
						self.languageDropDown.set("value", _language);
					}
				}
			}).show();
		},

		_changeLanguage: function(value) {
			var refreshAfternew = !!_compValues[_getSysId(value)];
			var langDropdownValue = this.languageDropDown.get("value");
			if (langDropdownValue !== value) {
				_setLanguage(value, langDropdownValue);
			} else {
				_setLanguage(value);
			}


			//prefill items with default value if its not an exception
			this.setCompValue();
			this._setFilterOptions(this.grid, value);

			this._updateGridWidgets();
			if (refreshAfternew) {
				// refresh grid so newly created items on previous refresh gets updated
				this.grid.refresh();
			}
			this.setMsg();
		},

		/**
		 * Mark deleted item unused
		 * @param {Object} [row]
		 */
		_onDeleteComposition: function(row) {
			this._use(row.data[COMPDATA], false);
			this._setDirty(true, true);
		},

		/**
		 * Return new object for listStore, creates new row with a first widget
		 * @param {Widget} w
		 */
		_onNewComposition: function(w) {
			var widget = null;
			if (w) {
				var editorArgs = this.getEditorArgs(w);
				widget = new w(editorArgs);
			}
			var data = {
				Language: _language,
				widgets: [widget]
			};

			return data;
		},

		/**
		 * Opens preview dialog
		 */
		_onPreview: function() {
			var self = this;
			this.setBaseCompValue();

			function getPreviewContent() {
				// process and returns the translated preview contents for the store
				var sysId = _getSysId();
				var content = [];
				var compArray = [];
				var baseValue = _compValues[BASE_COMPOSITION] || "[\"" + COMPEMPTY + "\"]";

				if (_compValues[sysId] || _compValues[BASE_COMPOSITION]) {
					compArray = JSON.parse(_compValues[sysId] || baseValue);

					var languageResults = _languageStore.query();
					compArray = [];

					_.each(languageResults, function(val) {
						compArray.push(_.extend(val, {compValue: JSON.parse(_compValues[val.SysID] || baseValue)}));
					});

					_.each(compArray, function(compLang) {
						if (compLang.compValue.length && compLang.compValue[0] != COMPEMPTY) {
							//if empty item then ignore it
							//create a store of all of languages so it cen be filtered based on selection
							_.each(compLang.compValue, function(val) {
								var cont = "";
								_.each(val, function(item) {
									if (item.value == null) {
										return;
									}
									var data = (item.type == "CompositionPlacement" ? _placementStore.get(_mapValueArray[item.value]) : _libraryStore.get(_mapValueArray[item.value]));
									var formatedData = data.Languages[this.SysID] || data.$Name;

									if (item.type == "Composition" && item.textValue) {
										formatedData = item.textValue + item.textSuffix + " " + formatedData;
									}

									cont += formatedData + "&nbsp;&nbsp;&nbsp;&nbsp;";
								}, this);

								content.push({
									id: content.length,
									Language: this.$,
									dataContent: cont
								});
							}, compLang);
						}
					});
				}

				return content;
			}

			function getPrevTitleLabel(l) {
				// returns the processed title of preview dialog
				var titleLabel = _languageStore.get(l || _language).Name;

				if (_languageStore.get(l || _language).labelSuffix) {
					titleLabel += " - " + locale.resource("CompositionEditorException");
				}

				return locale.resource("Preview") + " - " + titleLabel;
			}

			var contentStore = new Memory({
				data: getPreviewContent()
			});

			var previewGrid = new OnDemandGrid({
				"class": "csi-dgrid-manager csi-comp-editor-prev",
				columns: [{
					renderCell: function(column, value, node) {
						node.innerHTML = value.dataContent;
					},
					renderHeaderCell: function(th) {
						var nLanguages = _languageStore.query().length;

						if (nLanguages > 1) {
							var prevLangSelect = new Select({
								"class": "csi-header-select-dropdown",
								grid: this.grid,
								labelAttr: _languageStore.labelProperty,
								labelType: "html",
								sortByLabel: false,
								store: _languageStore,
								value: _language,
								onChange: function(val) {
									self._setFilterOptions(this.grid, val, true);
									prevDialog.updateTitle(val);
								}
							}).placeAt(th);

							self.updateLanguageDropdown(prevLangSelect);
						}
					}
				}],
				store: contentStore
			});

			this._setFilterOptions(previewGrid, _language, true);

			var prevDialog = new showDialog({
				buttons: [MessageDialog.BUTTON_CLOSE],
				title: getPrevTitleLabel(),
				grid: previewGrid,
				updateTitle: function(val) {
					this.set("title", getPrevTitleLabel(val));
				}
			});

			prevDialog.show();
		},

		/**
		 * Process on submit
		 * @param {Event} e
		 */
		_onSubmit: function(e) {
			this.setCompValue();
			this.inherited(arguments);
			this.applyToHistory = {};
			this.itemSelected = {};
		},

		/**
		 * Resets the Composition to base composition
		 * @param {String number} language
		 * @param {String} [newValue]
		 * @param {Boolean} [bResetComp]
		 */
		_resetComp: function(language, newValue, bResetComp) {
			var self = this;
			var sysId = (language == null) ? _language : language;
			var l = _getLanguageId(sysId);
			var valueArray = newValue ? JSON.parse(newValue) : [];

			if (!bResetComp) {
				_compValues[sysId] = _compValues[BASE_COMPOSITION];
			}

			this.listStore.query({Language: l}).forEach(function(val) {
				//remove all values
				self.listStore.remove(val[self.listStore.idProperty]);
			});

			if (valueArray.length) {
				//create a copy of newvalue to compare with saved values
				if (bResetComp) {
					if (valueArray[0] == COMPEMPTY) {
						_compValues[sysId] = newValue;
						return;
					}
					_compValues[sysId] = newValue;
				}

				_.each(valueArray, function(arr) {
					_.each(arr, function(arr1) {
						//replace all values with corresponding map value
						arr1.value = _mapValueArray[arr1.value];
					});
					self.createNewItem(l, arr);
				}, this);
			} else {
				this.listStore.query({Language: csi.EMPTY_URL}).forEach(function(val) {
					//repopulate all values from base
					self.createNewItem(l, _.clone(val.CompositionData));
				});
			}
		},

		/**
		 * Add select box to header to mass apply language to all column names
		 * @param {DomNode} th
		 */
		_renderCompositionHeader: function(th) {
			var nLanguages = _languageStore.query().length;

			if (nLanguages > 1) {
				this.languageDropDown = new Select({
					"class": "csi-header-select-dropdown",
					labelAttr: _languageStore.labelProperty,
					labelType: "html",
					sortByLabel: false,
					store: _languageStore,
					value: _language,
					onChange: lang.hitch(this, this._onChangeLanguage)
				}).placeAt(th);
			}

		},

		/**
		 * Adds reset button to header
		 * @param {DomNode} th
		 */
		_renderCompositionResetHeader: function(th) {
			var self = this;
			this.btnContainer = domConstruct.create("div", {
				"class": "csi-comp-btn-container"
			}, th);

			new Button({
				label: _.escape(locale.resource("BUTTON_APPLY_TO")),
				"class": "csi-link-btn csi-comp-applyto-btn",
				onClick: function() {
					// reset exception language to base value
					self._applyToComp();
					self._setDirty();
				}
			}).placeAt(this.btnContainer);

			new ToolbarSeparator().placeAt(this.btnContainer);

			new Button({
				label: _.escape(locale.resource("CompositionResetException")),
				"class": "csi-link-btn csi-comp-reset-btn",
				onClick: function() {
					// reset exception language to base value
					_resetException = true;
					self._resetComp(_getSysId(_language));
					self._setDirty();

				}
			}).placeAt(this.btnContainer);
		},

		/**
		 * Recreate widgets from CompositeData
		 */
		_renderWidgetCell: function(column, value, node) {
			var data = {
				id: value.$URL || value.id,
				data: value
			};

			if ((!column.widgets || !column.widgets.length) && column.CompositionData) {
				column.widgets = [];

				_.each(column.CompositionData, function(cell) {
					if (cell.type === "Composition") {
						this.grid.onAddMaterial(data, cell, true);
					} else {
						this.grid.onAddPlacement(data, cell, true);
					}
				}, this);
			}

			var w = value.widgets ? value.widgets[this.id] : null;
			if (w) {
				w.placeAt(node);
			}
		},

		/**
		 * sets the editor dirty for changes
		 * @param {Boolean} bDelete
		 * @param {String} msg
		 */
		_setDirty: function(bDelete, bChanged, msg) {
			this.setCompValue(bDelete, bChanged);
			this.setMsg(msg);
			this.grid.refresh();
			this.dirty = true;
		},

		/**
		 * Sets the grid sort and filter options
		 * @param {Dgrid} grid
		 * @param {String} language
		 * @param {Boolean} bSort
		 */
		_setFilterOptions: function(grid, language, bSort) {
			grid.query = {Language: language};
			grid.refresh();
		},

		/**
		 * Reset widget values to update it with updated label values
		 */
		_updateGridWidgets: function() {
			var resetLangValue = function(items) {
				_.each(items, function(val) {

					if (val.widgets) {
						resetLangValue(val.widgets);
					} else {

						if ((val.CompositionData && val.CompositionData.length) || val.Language) {
							return;
						}

						var widgetValue = val.get("value");
						val.set("value", widgetValue);
					}
				});
			};

			resetLangValue(this.grid.store.query({}));
		},

		/**
		 * Mark library item as (un)used
		 * @param {String} id
		 * @param {Boolean} b
		 */
		_use: function(id, b) {
			var comp = _libraryStore.get(id);
			if (comp && comp[_libraryStore.labelProperty]) {
				comp._used = b;
			}
		}
	});

	return declare([Controller], {
		_dialog: null,

		/**
		 * Open composition dialog
		 */
		preProcess: function() {
			var ac = this;

			this.setProgress();

			var node = this.getNode();
			var listData = [];
			// initialize como values from node
			_compValues = _.clone(node.CompositionLines) || {};
			_mapValueArray = _.values(node.CompositionElementsMap) || [];

			// create store from string data
			_.each(node.CompositionLines, function(val, i) {
				try {
					var parsedValues = JSON.parse(val);

					if (parsedValues && JSON.parse(val)[0] && !JSON.parse(val)[0].length) {
						// fix for parsed value not inside an array
						parsedValues = [parsedValues];
					}

					parsedValues.forEach(function(item) {
						if (item != COMPEMPTY) {
							listData.push({
								SysID: i,
								CompositionData: item,
								id: comm.createUrl(COMP)
							});
						}
					});
				} catch (e) {
					console.log(e);
				}
			});

			//create default data from copy of any existing exception data
			if (!node.CompositionLines[0] && listData.length) {
				listData.push({
					SysID: 0,
					CompositionData: listData[0].CompositionData,
					id: comm.createUrl(COMP)
				});
			}

			this.listStore = new Observable(new Memory({
				data: listData
			}));

			storage.set(STORAGE_LANGUAGE, csi.EMPTY_URL);
			_language = csi.EMPTY_URL;
			var s = new Search();
			s.byType(LANGUAGE);
			s.getQuery().SortByAttr("SortOrder");

			_languageStore = new Names({
				bSort: false,
				data: [csiStore.nameObject(locale.resource("AllLanguages"))],
				request: s.getRequest(),
				customOperation: true,
				_processResultNode: function(/*Node*/node, /*Cache*/cache) {
					return {$: node.$URL, Name: node.$Name, SysID: node.SysID, node: node};
				},
				typeNode: LANGUAGE
			});

			var libraryStore = new Nodes({
				bSimpleQueryEngine: true,
				sortQuery: new Query().SortByNode(xml.PROP_NAME),
				typeNode: COMP
			});

			var placementStore = new Nodes({
				bSimpleQueryEngine: true,
				sortQuery: new Query().SortByNode(xml.PROP_NAME),
				typeNode: COMPPLACEMENT
			});

			var params = {
				listStore: this.listStore,
				title: this.getTitle(),
				value: this.oldValue,
				onSave: lang.hitch(this, this._onSave),
				onCancel: lang.hitch(this, this._onCancel)
			};

			// make sure both stores are queried before building grid

			when(_languageStore.query(), function(languageResults) {
				when(ac.listStore.query(), function(listResults) {
					_.each(listResults, function(value) {
						//parse composition line string to JSON
						value.widgets = [];

						value.Language = _getLanguageId(value.SysID);

						var data = value.CompositionData;
						//Replace all values with the corresponding values from CompositionElementsMap
						var replaceValuesbyMap = function(val) {
							if (_.isArray(val)) {
								_.each(val, function(val1) {
									replaceValuesbyMap(val1);
								});
							} else {
								val.value = node.CompositionElementsMap[val.value];
							}
						};

						replaceValuesbyMap(data);
					});
				});

				var placementDfd = placementStore.query();
				var libraryDfd = libraryStore.query();

				// only create the dialog after querying stories - for some reason it does
				// not work to create the grid while store is being queried
				all([placementDfd, libraryDfd]).then(function(results) {
					var placementResults = results[0];
					var libraryResults = results[1];

					_libraryStore = new Memory({
						idProperty: "$URL",
						data: libraryResults
					});

					_libraryTotal = libraryResults.length;

					_placementStore = new Memory({
						idProperty: "$URL",
						data: placementResults
					});

					// if no selected language, then select first
					_setLanguage(_language);

					ac._dialog = new _Dialog(params);
					ac._dialog.startup();
					ac._dialog.show();
					ac.setProgress(null);
					ac._dialog.savedData = _.clone(node.CompositionLines) || {};
					ac._dialog._setFilterOptions(ac._dialog.grid, _language, true);
				});
			});
		},

		/**
		 * Populate CompositionLines and CompositionElementsMap
		 */
		toWriter: function() {
			var w = this.m_writer;
			var cache = this.m_cache;
			var store = this.listStore;

			store.query({}).map(function(node) {
				// remove node from cache - it should be added back if operation succeeds
				if (xml.isClientUrl(node.$URL)) {
					cache.remove(node.$URL);
				}
			});

			// cleanup CompValues for duplicates of base composition and empty values
			if (!_isBaseCompositionEmpty()) {
				_.each(_compValues, function(val, i) {
					if (!_compValues[i] || (i != 0 && val === _compValues[BASE_COMPOSITION]) || !_.flatten(JSON.parse(val)).length) {
						delete _compValues[i];
					}
				});
			} else {
				_compValues[BASE_COMPOSITION] = "[\"" + COMPEMPTY + "\"]"; // make sure base stays empty
			}

			var valueMap = _mapValueArray.reduce(function(map, val, i) {
				map[i++] = val;
				return map;
			}, {});

			this._dialog.initialComp = _.clone(_compValues);
			w.changeNode(this.m_urlNode)
				.changeAttribute(COMPLINES, xml.ATTR_STRINGMAP, _compValues)
				.changeAttribute(COMPELEMAP, xml.ATTR_REFMAP, valueMap);

			this.inherited(arguments);

		},

		/**
		 * Close the dialog
		 * @param {Boolean} bStatus
		 * @param {Boolean} bRepeat
		 */
		postProcess: function(bStatus, bRepeat) {
			if (!this._dialog.closeOnSave) {
				bRepeat = true;
				this._dialog.closeOnSave = true;
				this._dialog.dirty = false;
				this._dialog.resumeAfterSave();
			}
			if (this._dialog && !bRepeat) {
				this._dialog.hide();
				this.resumeProcess();
			}

			this.inherited(arguments, [bStatus, bRepeat]);
		},

		postProcessSuccess: function() {
			const res = this.inherited(arguments);
			this._dialog?.grid.refresh();
			this.widget.getSiblingView("ExceptionLanguageCompositionStrings")?.refreshData();
			return res;
		},

		/**
		 * Process new attribute value
		 * @param {Object} value
		 */
		_onSave: function(value) {
			this.newValue = value;
			this.process();
		},

		_onCancel: function() {
			this._dialog.closeOnSave = true;
			this.postProcess();
		}

	});
});
